import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiGrid, FiBarChart2, FiFileText, FiFile, FiPlus, FiInbox,
  FiSettings, FiUser, FiShield, FiSliders, FiLogOut, FiBell,
  FiSearch, FiTrendingUp, FiTrendingDown, FiUsers, FiEye,
  FiDollarSign, FiChevronDown, FiCheckCircle, FiActivity, FiMenu,
} from 'react-icons/fi';
import './AdminDashboard.css';
import CreatePageView from './views/CreatePageView';
import AllPagesView from './views/AllPagesView';
import './views/views.css';

const navGroups = [
  {
    title: 'Dashboard',
    items: [
      { id: 'overview',  label: 'Overview',  icon: FiGrid },
      { id: 'analytics', label: 'Analytics', icon: FiBarChart2 },
      { id: 'reports',   label: 'Reports',   icon: FiFileText },
    ],
  },
  {
    title: 'Pages',
    items: [
      { id: 'all-pages',   label: 'All Pages',       icon: FiFile },
      { id: 'create-page', label: 'Create New Page', icon: FiPlus },
      { id: 'drafts',      label: 'Drafts',          icon: FiInbox },
    ],
  },
  {
    title: 'Settings',
    items: [
      { id: 'profile',     label: 'Profile Settings',    icon: FiUser },
      { id: 'security',    label: 'Security',            icon: FiShield },
      { id: 'preferences', label: 'System Preferences',  icon: FiSliders },
    ],
  },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeItem, setActiveItem] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem('astro_admin_auth') !== '1') {
      navigate('/admin/login', { replace: true });
      return;
    }
    if (sessionStorage.getItem('astro_admin_welcome') === '1') {
      setShowWelcome(true);
      sessionStorage.removeItem('astro_admin_welcome');
    }
  }, [navigate]);

  const handleLogout = () => {
    sessionStorage.removeItem('astro_admin_auth');
    navigate('/admin/login', { replace: true });
  };

  const activeLabel = navGroups
    .flatMap((g) => g.items)
    .find((i) => i.id === activeItem)?.label || 'Overview';

  return (
    <div className="ad-shell">
      {/* Sidebar */}
      <aside className={`ad-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="ad-logo">
          <div className="ad-logo-mark"><FiShield /></div>
          <div>
            <div className="ad-logo-name">Astro Admin</div>
            <div className="ad-logo-tag">Control Plane</div>
          </div>
        </div>

        <nav className="ad-nav">
          {navGroups.map((group) => (
            <div key={group.title} className="ad-nav-group">
              <div className="ad-nav-title">{group.title}</div>
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    className={`ad-nav-item ${activeItem === item.id ? 'active' : ''}`}
                    onClick={() => { setActiveItem(item.id); setSidebarOpen(false); }}
                  >
                    {activeItem === item.id && <span className="ad-nav-active-bar" />}
                    <Icon /> <span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          ))}

          <div className="ad-nav-group">
            <button
              className="ad-nav-item ad-nav-logout"
              onClick={() => setShowLogoutConfirm(true)}
            >
              <FiLogOut /> <span>Logout</span>
            </button>
          </div>
        </nav>
      </aside>

      {sidebarOpen && (
        <div className="ad-sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main */}
      <div className="ad-main">
        <header className="ad-topbar">
          <button className="ad-burger" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
            <FiMenu />
          </button>
          <div className="ad-topbar-title">
            <h1>{activeLabel}</h1>
            <p>Welcome back, monitor your operations at a glance</p>
          </div>
          <div className="ad-search">
            <FiSearch />
            <input placeholder="Search pages, users, reports..." />
          </div>
          <button className="ad-icon-btn ad-bell" aria-label="Notifications">
            <FiBell />
            <span className="ad-bell-dot" />
          </button>
          <button className="ad-profile">
            <div className="ad-avatar">A</div>
            <div className="ad-profile-meta">
              <div className="ad-profile-name">Admin</div>
              <div className="ad-profile-role">Super Admin</div>
            </div>
            <FiChevronDown />
          </button>
        </header>

        <main className="ad-content">
          {activeItem === 'overview' && (
            <OverviewView onCreateNew={() => setActiveItem('create-page')} />
          )}

          {activeItem === 'create-page' && (
            <CreatePageView />
          )}

          {activeItem === 'all-pages' && (
            <AllPagesView onCreateNew={() => setActiveItem('create-page')} />
          )}

          {activeItem === 'drafts' && (
            <AllPagesView filterStatus="draft" onCreateNew={() => setActiveItem('create-page')} />
          )}

          {['analytics', 'reports', 'profile', 'security', 'preferences'].includes(activeItem) && (
            <ComingSoonView label={activeLabel} />
          )}
        </main>
      </div>

      {/* Welcome popup */}
      <AnimatePresence>
        {showWelcome && (
          <motion.div
            className="ad-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="ad-modal"
              initial={{ opacity: 0, y: 20, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ type: 'spring', damping: 22, stiffness: 240 }}
            >
              <div className="ad-modal-burst"><FiCheckCircle /></div>
              <h2>Welcome Admin</h2>
              <p className="ad-modal-text">Dashboard Access Granted</p>
              <p className="ad-modal-sub">You're securely signed in. Your session is now active.</p>
              <button className="ad-btn primary" onClick={() => setShowWelcome(false)}>
                Continue to Dashboard
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Logout confirm */}
      <AnimatePresence>
        {showLogoutConfirm && (
          <motion.div
            className="ad-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="ad-modal"
              initial={{ opacity: 0, y: 20, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ type: 'spring', damping: 22, stiffness: 240 }}
            >
              <div className="ad-modal-icon warn"><FiLogOut /></div>
              <h2>Confirm Sign Out</h2>
              <p className="ad-modal-sub">
                You're about to end your admin session. Any unsaved changes will be lost.
              </p>
              <div className="ad-modal-actions">
                <button className="ad-btn ghost" onClick={() => setShowLogoutConfirm(false)}>
                  Cancel
                </button>
                <button className="ad-btn danger" onClick={handleLogout}>
                  <FiLogOut /> Sign Out
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const fmtNum = (n) => Number(n || 0).toLocaleString();

const relativeTime = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d} day${d === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString();
};

const OverviewView = ({ onCreateNew }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    setError('');
    fetch('/api/pages/stats')
      .then((r) => r.json())
      .then((j) => {
        if (j.success) setData(j.data);
        else setError(j.error || 'Failed to load stats');
      })
      .catch((err) => setError(err.message || 'API unreachable'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="ad-stats-loading">
        <span className="vw-spinner vw-spinner-lg" />
        <p>Loading dashboard…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ad-card" style={{ textAlign: 'center', padding: 40 }}>
        <h2 style={{ color: '#f87171', marginBottom: 8 }}>Couldn't load stats</h2>
        <p style={{ color: '#8089a3', marginBottom: 18 }}>{error}</p>
        <button className="ad-btn ghost" style={{ flex: 'none', padding: '10px 18px' }} onClick={load}>
          Retry
        </button>
      </div>
    );
  }

  if (!data || data.totalPages === 0) {
    return (
      <div className="ad-card" style={{ textAlign: 'center', padding: '60px 30px' }}>
        <div style={{
          width: 64, height: 64, margin: '0 auto 18px',
          background: 'linear-gradient(135deg, #6c2bd9, #0ea5e9)',
          borderRadius: 16, display: 'grid', placeItems: 'center',
          color: '#fff', fontSize: 28,
        }}>
          <FiFile />
        </div>
        <h2 style={{ color: '#e7e9f0', marginBottom: 8 }}>No pages yet</h2>
        <p style={{ color: '#8089a3', marginBottom: 22, maxWidth: 460, marginLeft: 'auto', marginRight: 'auto' }}>
          Your dashboard will populate as you generate programmatic SEO pages. Create your first one to see real stats here.
        </p>
        {onCreateNew && (
          <button
            className="ad-btn primary"
            style={{ flex: 'none', padding: '12px 24px', display: 'inline-flex' }}
            onClick={onCreateNew}
          >
            <FiPlus /> Create First Page
          </button>
        )}
      </div>
    );
  }

  const liveStats = [
    {
      label: 'Total Pages', value: fmtNum(data.totalPages), icon: FiFile, color: '#6c2bd9',
      change: data.pagesChange,
      changeText: data.pagesChange.value === 0 ? null : `${data.pagesChange.value > 0 ? '+' : ''}${data.pagesChange.value}%`,
    },
    {
      label: 'Total Words', value: fmtNum(data.totalWords), icon: FiBarChart2, color: '#0ea5e9',
      change: data.wordsChange,
      changeText: data.wordsChange.value === 0 ? null : `${data.wordsChange.value > 0 ? '+' : ''}${data.wordsChange.value}%`,
    },
    {
      label: 'Pages This Week', value: fmtNum(data.pagesThisWeek), icon: FiActivity, color: '#22c55e',
      change: null,
      changeText: `${fmtNum(data.byStatus.published)} published · ${fmtNum(data.byStatus.draft)} draft`,
    },
    {
      label: 'Avg Words / Page', value: fmtNum(data.avgWords), icon: FiFileText, color: '#f59e0b',
      change: null,
      changeText: `${fmtNum(data.totalFaqs)} total FAQs`,
    },
  ];

  const categoryEntries = Object.entries(data.perCategory);
  const lineSeries = data.perDay.map((d) => d.count);
  // Sample labels every 5 days to avoid crowding
  const lineLabels = data.perDay.map((d, i) => (i % 5 === 0 || i === data.perDay.length - 1 ? d.label : ''));
  const barSeries = categoryEntries.map(([, count]) => count);
  const barLabels = categoryEntries.map(([label]) => label);

  return (
    <>
      <motion.section
        className="ad-stats"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {liveStats.map((s, i) => {
          const Icon = s.icon;
          const trend = s.change?.trend;
          const Trend = trend === 'up' ? FiTrendingUp : trend === 'down' ? FiTrendingDown : null;
          return (
            <motion.div
              key={s.label}
              className="ad-stat"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
            >
              <div className="ad-stat-icon" style={{ background: `${s.color}22`, color: s.color }}>
                <Icon />
              </div>
              <div className="ad-stat-body">
                <div className="ad-stat-label">{s.label}</div>
                <div className="ad-stat-value">{s.value}</div>
                {s.changeText && (
                  <div className={`ad-stat-trend ${trend || ''}`}>
                    {Trend && <Trend />} {s.changeText}
                    {trend && <span>vs last week</span>}
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </motion.section>

      <section className="ad-grid">
        <motion.div
          className="ad-card"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <div className="ad-card-head">
            <div>
              <h2>Generation Velocity</h2>
              <p>Pages generated per day · last 30 days</p>
            </div>
            <div className="ad-pill">{fmtNum(data.pagesThisWeek)} this week</div>
          </div>
          {lineSeries.some((v) => v > 0) ? (
            <LineChart data={lineSeries} labels={lineLabels} />
          ) : (
            <div className="ad-chart-empty">No pages generated in the last 30 days.</div>
          )}
        </motion.div>

        <motion.div
          className="ad-card"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          <div className="ad-card-head">
            <div>
              <h2>Pages by Category</h2>
              <p>{categoryEntries.length} of 4 categories in use</p>
            </div>
            <div className="ad-pill">{fmtNum(data.totalPages)} total</div>
          </div>
          {barSeries.length > 0 ? (
            <BarChart data={barSeries} labels={barLabels} />
          ) : (
            <div className="ad-chart-empty">No categories yet.</div>
          )}
        </motion.div>
      </section>

      <motion.section
        className="ad-card"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.4 }}
      >
        <div className="ad-card-head">
          <div>
            <h2>Recent Activity</h2>
            <p>Latest pages generated on this account</p>
          </div>
          {onCreateNew && (
            <button className="ad-text-btn" onClick={onCreateNew}>+ New page</button>
          )}
        </div>
        <div className="ad-table-wrap">
          <table className="ad-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Action</th>
                <th>Target</th>
                <th>Words</th>
                <th>Time</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.recent.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div className="ad-user">
                      <div className="ad-avatar sm">A</div>
                      <span>Admin</span>
                    </div>
                  </td>
                  <td>Generated page</td>
                  <td className="muted">
                    <a
                      href={`/blog/${r.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: '#c4b5fd', textDecoration: 'none' }}
                    >
                      {r.title}
                    </a>
                    {' '}
                    <span style={{ opacity: 0.6 }}>· {r.categoryLabel}</span>
                  </td>
                  <td className="muted">{fmtNum(r.wordCount)}</td>
                  <td className="muted">{relativeTime(r.createdAt)}</td>
                  <td>
                    <span className={`ad-status ${r.status === 'published' ? 'success' : r.status === 'draft' ? 'pending' : 'error'}`}>
                      {r.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.section>
    </>
  );
};

const ComingSoonView = ({ label }) => (
  <div className="vw">
    <div className="vw-card vw-coming-soon">
      <h2>{label}</h2>
      <p>This section is on the roadmap. The skeleton is in place — wire it up to your data source whenever you're ready.</p>
    </div>
  </div>
);

const LineChart = ({ data, labels }) => {
  const w = 600, h = 200, p = 28;
  const max = (Math.max(...data, 1)) * 1.15;
  const step = data.length > 1 ? (w - p * 2) / (data.length - 1) : 0;
  const points = data.map((v, i) => [p + i * step, h - p - (v / max) * (h - p * 2)]);
  const path = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x} ${y}`).join(' ');
  const fill = `${path} L ${points[points.length - 1][0]} ${h - p} L ${p} ${h - p} Z`;
  const tickLabels = labels || ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="ad-svg-chart" preserveAspectRatio="none">
      <defs>
        <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor="#6c2bd9" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#6c2bd9" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="lineStroke" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"  stopColor="#a855f7" />
          <stop offset="100%" stopColor="#0ea5e9" />
        </linearGradient>
      </defs>
      {[0, 1, 2, 3].map((i) => (
        <line
          key={i}
          x1={p} x2={w - p}
          y1={p + (i * (h - p * 2)) / 3}
          y2={p + (i * (h - p * 2)) / 3}
          stroke="rgba(255,255,255,0.05)"
        />
      ))}
      <motion.path
        d={fill}
        fill="url(#lineGrad)"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.3 }}
      />
      <motion.path
        d={path}
        fill="none"
        stroke="url(#lineStroke)"
        strokeWidth="2.5"
        strokeLinejoin="round"
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1.2, ease: 'easeInOut' }}
      />
      {points.map(([x, y], i) => (
        <motion.circle
          key={i}
          cx={x} cy={y} r="3"
          fill="#0b0d18" stroke="#a855f7" strokeWidth="2"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: 0.6 + i * 0.04 }}
        />
      ))}
      {tickLabels.map((m, i) =>
        m ? (
          <text key={i} x={p + i * step} y={h - 8} textAnchor="middle" fontSize="10" fill="#6c7388">{m}</text>
        ) : null
      )}
    </svg>
  );
};

const BarChart = ({ data, labels }) => {
  const w = 600, h = 200, p = 28;
  const max = (Math.max(...data, 1)) * 1.15;
  const bw = data.length > 0 ? (w - p * 2) / data.length : 0;
  const tickLabels = labels || ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="ad-svg-chart" preserveAspectRatio="none">
      <defs>
        <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor="#0ea5e9" />
          <stop offset="100%" stopColor="#6c2bd9" />
        </linearGradient>
      </defs>
      {[0, 1, 2, 3].map((i) => (
        <line
          key={i}
          x1={p} x2={w - p}
          y1={p + (i * (h - p * 2)) / 3}
          y2={p + (i * (h - p * 2)) / 3}
          stroke="rgba(255,255,255,0.05)"
        />
      ))}
      {data.map((v, i) => {
        const bh = (v / max) * (h - p * 2);
        const x = p + i * bw + bw * 0.2;
        const y = h - p - bh;
        return (
          <g key={i}>
            <motion.rect
              x={x} width={bw * 0.6}
              fill="url(#barGrad)"
              rx="6"
              initial={{ y: h - p, height: 0 }}
              animate={{ y, height: bh }}
              transition={{ duration: 0.6, delay: 0.2 + i * 0.06, ease: [0.22, 1, 0.36, 1] }}
            />
            <text x={p + i * bw + bw / 2} y={h - 8} textAnchor="middle" fontSize="10" fill="#6c7388">{tickLabels[i]}</text>
          </g>
        );
      })}
    </svg>
  );
};

export default AdminDashboard;
